'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { useCalendly } from '@/components/CalendlyPopup';

const settleEase = [0.16, 1, 0.3, 1] as const;

type Props = {
  reducedMotion: boolean;
  visual: React.ReactNode;
};

export default function HeroContent({ reducedMotion, visual }: Props) {
  const { open } = useCalendly();
  const enter = (delay: number) =>
    reducedMotion
      ? {
          initial: false as const,
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0 },
        }
      : {
          initial: { opacity: 0, y: 22 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, ease: settleEase, delay },
        };

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,max-content)_minmax(220px,1fr)] lg:items-center lg:gap-8 xl:gap-12">
      <div className="min-w-0">
        <motion.h1
          className="w-max max-w-full font-semibold leading-[0.88] tracking-[-0.045em] text-white text-[clamp(1.95rem,8.6vw,2.6rem)] sm:text-[clamp(3rem,6.2vw,3.5rem)] md:text-[clamp(3.75rem,7.4vw,4.75rem)] lg:text-[clamp(4.4rem,6.6vw,5.6rem)] xl:text-[clamp(5.6rem,6.85vw,7.15rem)]"
          initial={reducedMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.92, ease: settleEase }}
        >
          <span className="block">We build products</span>
          <span className="block">that move</span>
          <span className="block">businesses</span>
          <span className="block">
            forward<span className="text-[#2563FF]">.</span>
          </span>
        </motion.h1>

        <motion.div className="mt-8 max-w-md lg:mt-10" {...enter(0.12)}>
          <p className="text-[15px] leading-relaxed text-[#E5E7EB]/80 sm:text-base">
            Creator Tech is a product engineering studio for ambitious teams. We turn ideas, problems
            and opportunities into digital products people actually use.
          </p>
          <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={open}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563FF] px-6 py-3.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#2203C5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C8CFF]"
            >
              Start a Project
              <span aria-hidden="true">→</span>
            </button>
            <a
              href="#how-we-build"
              className="inline-flex items-center justify-center gap-2 rounded-full px-2 py-3.5 text-sm font-medium text-white/75 transition-colors duration-200 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C8CFF]"
            >
              See How We Build
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none mt-10 w-full lg:mt-0">{visual}</div>
    </div>
  );
}
